import { EntityMap, moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { GroupSuggestedMembersDismissFulfilledAction } from '../../domains/groupSuggestedMemberDismiss/actions.types';
import {
  GroupSuggestedMembersLoadAction,
  GroupSuggestedMembersLoadMoreAction,
  GroupSuggestedMembersLoadMoreFulfilledAction,
} from '../../domains/groupSuggestedMembers/actions.types';
import { Group, User } from '../types';

import { GroupSuggestedMembers, SuggestedMember, SuggestedMembersPerGroup } from './types';

export const initialState: GroupSuggestedMembers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

type LoadMoreGroupSuggestedMembersFulfilled = (
  state: GroupSuggestedMembers,
  payload: GroupSuggestedMembersLoadMoreFulfilledAction['payload']
) => EntityMap<SuggestedMembersPerGroup>;
const loadMoreGroupSuggestedMembersFulfilled: LoadMoreGroupSuggestedMembersFulfilled = (
  state,
  { groupId, entities }
) => {
  const groupSuggestedMembers = state.fulfilled.byId[groupId];
  const newSuggestedMembers: SuggestedMember[] = entities.groupSuggestedMembers[groupId].suggestedMembers.reduce<
    SuggestedMember[]
  >((suggestedMembers, suggestedMember: SuggestedMember) => {
    if (
      !groupSuggestedMembers.suggestedMembers.find((member) => member.nodeId === suggestedMember.nodeId) &&
      !groupSuggestedMembers.dismissedSuggestedMembers.includes(suggestedMember.nodeId)
    ) {
      return [...suggestedMembers, suggestedMember];
    }

    return suggestedMembers;
  }, []);

  const updatedGroupSuggestedMembers = {
    ...groupSuggestedMembers,
    suggestedMembers: [...groupSuggestedMembers.suggestedMembers, ...newSuggestedMembers],
  };

  return update(state, groupId, updatedGroupSuggestedMembers);
};

type DismissGroupSuggestedMember = (
  state: GroupSuggestedMembers,
  groupId: Group['id'],
  userId: User['id']
) => EntityMap<SuggestedMembersPerGroup>;
const dismissGroupSuggestedMember: DismissGroupSuggestedMember = (state, groupId, userId) => {
  const groupSuggestedMembers = state.fulfilled.byId[groupId];

  if (!groupSuggestedMembers) {
    return state;
  }

  const updatedGroupSuggestedMembers = {
    ...groupSuggestedMembers,
    suggestedMembers: groupSuggestedMembers.suggestedMembers.filter(
      (suggestedMember) => suggestedMember.nodeId !== userId
    ),
    dismissedSuggestedMembers: [...groupSuggestedMembers.dismissedSuggestedMembers, userId],
  };

  return update(state, groupId, updatedGroupSuggestedMembers);
};

export type GroupSuggestedMembersAction =
  | GroupSuggestedMembersLoadAction
  | GroupSuggestedMembersLoadMoreAction
  | GroupSuggestedMembersDismissFulfilledAction;

const reducer: Reducer<GroupSuggestedMembers, GroupSuggestedMembersAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_SUGGESTED_MEMBERS.LOAD_PENDING':
      return moveToPending(state, [action.payload.groupId]);
    case 'GROUP_SUGGESTED_MEMBERS.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.groupId], action.payload.error);
    case 'GROUP_SUGGESTED_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupSuggestedMembers);
    case 'GROUP_SUGGESTED_MEMBERS.LOAD_MORE_FULFILLED':
      return loadMoreGroupSuggestedMembersFulfilled(state, action.payload);
    case 'GROUP_SUGGESTED_MEMBERS.DISMISS_FULFILLED':
      return dismissGroupSuggestedMember(state, action.payload.groupId, action.payload.userId);
    default:
      return state;
  }
};

export default reducer;
